import type { ComponentPropsWithoutRef, ElementType, ReactNode } from 'react'
import './FormLink.css'

export type FormLinkProps<T extends ElementType = 'a'> = {
  as?: T
  children: ReactNode
} & Omit<ComponentPropsWithoutRef<T>, 'as' | 'children'>

/**
 * Lien discret de formulaire (ex. « Mot de passe oublié »). Polymorphe comme
 * Button : `as="button"` en fait une action de type lien plutôt qu'une vraie
 * navigation — utile pour « Ajouter une ligne » / « Supprimer », aujourd'hui
 * des `<button>` sans style dans les listes répétables du back-office.
 */
export function FormLink<T extends ElementType = 'a'>({ as, children, className, ...rest }: FormLinkProps<T>) {
  const Component = (as || 'a') as ElementType
  const classes = ['azoya-form-link', className].filter(Boolean).join(' ')
  // Un <button> sans `type` explicite vaut `submit` en HTML. Or le cas d'usage
  // annoncé juste au-dessus est l'action DANS un formulaire (« Ajouter une
  // ligne », « Supprimer ») : l'appelant qui oublie `type="button"`
  // soumettrait le formulaire au lieu d'agir sur lui. La valeur est posée
  // avant `rest`, donc un appelant qui veut vraiment un bouton de soumission
  // peut toujours passer type="submit" et gagner.
  const typeParDefaut = Component === 'button' ? { type: 'button' as const } : undefined
  return (
    <Component className={classes} {...typeParDefaut} {...rest}>
      {children}
    </Component>
  )
}
